import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ClipboardCheck, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { datenabgleichAPI } from '@/lib/api';

/* Datenabgleich aus der App (28.09.2026): Das Mitglied hat nach dem ersten Login
   seine Daten geprüft. Korrekturen werden hier gezeigt und vom Empfang übernommen
   oder verworfen — nie still in den Lead geschrieben (Handy und E-Mail sind
   Login-Schlüssel). Gemeldete Kinder laufen als Familien-Hinweis (Tab Familie). */
const FELD_NAMEN = { name: 'Name', phone: 'Handynummer', email: 'E-Mail', birthday: 'Geburtsdatum', address: 'Straße', city: 'PLZ / Ort' };
const zeit = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

export default function DatenabgleichPanel({ leadId, onChange }) {
  const [daten, setDaten] = useState(null);
  const [auswahl, setAuswahl] = useState({});
  const [busy, setBusy] = useState(false);

  const laden = useCallback(async () => {
    try {
      const r = await datenabgleichAPI.jeLead(leadId);
      setDaten(r.data);
      const offen = (r.data.aenderungen || []).find(a => a.status === 'offen');
      setAuswahl(offen ? Object.fromEntries(Object.keys(offen.felder || {}).map(f => [f, true])) : {});
    } catch { setDaten(null); }
  }, [leadId]);
  useEffect(() => { laden(); }, [laden]);

  if (!daten) return null;
  const offen = (daten.aenderungen || []).find(a => a.status === 'offen');
  const stand = daten.stand || {};
  if (!offen && !stand.bestaetigt_am) return null;

  const entscheiden = async (was) => {
    setBusy(true);
    try {
      if (was === 'uebernehmen') {
        const felder = Object.keys(auswahl).filter(f => auswahl[f]);
        const r = await datenabgleichAPI.uebernehmen(offen.id, felder);
        toast.success(r.data.uebernommen.length ? `Übernommen: ${r.data.uebernommen.map(f => FELD_NAMEN[f] || f).join(', ')}` : 'Abgleich geschlossen, nichts übernommen');
      } else {
        await datenabgleichAPI.verwerfen(offen.id);
        toast.success('Korrekturen verworfen, Lead bleibt wie er ist');
      }
      await laden(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Entscheidung fehlgeschlagen'); }
    setBusy(false);
  };

  return (
    <Card data-testid="datenabgleich-panel" className={offen ? 'border-amber-300' : ''}>
      <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-1.5"><ClipboardCheck className="h-4 w-4" /> Datenabgleich aus der App</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        {stand.bestaetigt_am && <p className="text-xs text-muted-foreground">Vom Mitglied bestätigt am {zeit(stand.bestaetigt_am)}{stand.uebernommen_am ? ` · Korrekturen übernommen am ${zeit(stand.uebernommen_am)}` : ''}.</p>}
        {offen && (
          <>
            <p className="text-xs">Das Mitglied hat am {zeit(offen.erstellt)} Korrekturen gemeldet. Haken setzen, was übernommen werden soll:</p>
            <div className="space-y-1.5">
              {Object.entries(offen.felder || {}).map(([f, v]) => (
                <label key={f} className="flex items-start gap-2 cursor-pointer" data-testid={`abgleich-${f}`}>
                  <input type="checkbox" className="mt-1 accent-marke" checked={!!auswahl[f]} onChange={e => setAuswahl(p => ({ ...p, [f]: e.target.checked }))} />
                  <span><span className="font-medium">{FELD_NAMEN[f] || f}:</span> <span className="line-through text-muted-foreground">{v.alt || '–'}</span> → <span className="font-semibold">{v.neu || '–'}</span></span>
                </label>
              ))}
              {(offen.kinder_neu || []).map((k, i) => (
                <p key={i} className="text-xs text-muted-foreground pl-6">Kind gemeldet: {k.name}{k.birthday ? `, geb. ${k.birthday}` : ''} · verknüpfen im Tab Familie</p>
              ))}
              {offen.hinweis && <p className="text-xs text-muted-foreground pl-6">Hinweis: {offen.hinweis}</p>}
            </div>
            <div className="flex gap-2">
              <Button size="sm" disabled={busy} onClick={() => entscheiden('uebernehmen')} data-testid="abgleich-uebernehmen">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Übernehmen'}</Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => entscheiden('verwerfen')} data-testid="abgleich-verwerfen">Verwerfen</Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
